// VideoProduction.jsx
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./VideoProduction.css";

const NAV_OFFSET = 86;

// Works for Vite (VITE_*) and CRA (REACT_APP_*)
const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_CONTACT_API_URL) ||
  process.env.REACT_APP_CONTACT_API_URL ||
  "https://xjref277ki.execute-api.us-west-2.amazonaws.com";

// If your API expects a path like "/contact" or "/prod/contact", change it here:
const CONTACT_ENDPOINT = API_BASE;

const VideoEmbed = ({ id, title }) => (
  <div className="psoc-video">
    <iframe
      src={`https://www.youtube.com/embed/${id}`}
      title={title}
      frameBorder="0"
      loading="lazy"
      referrerPolicy="strict-origin-when-cross-origin"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  </div>
);

const PackageCard = ({ title, price, blurb, bullets, onSelect }) => (
  <div className="vp-card">
    <div className="vp-cardTop">
      <h3 className="vp-cardTitle">{title}</h3>
      <div className="vp-price">{price}</div>
    </div>

    <p className="vp-cardBlurb">{blurb}</p>

    <ul className="vp-list">
      {bullets.map((b) => (
        <li key={b}>{b}</li>
      ))}
    </ul>

    <button className="psoc-btn psoc-btnSecondary vp-cardBtn" type="button" onClick={onSelect}>
      Get This Package
    </button>
  </div>
);

const VideoProduction = () => {
  const contactRef = useRef(null);

  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ state: "idle", message: "" }); // idle | sending | success | error
  const [subject, setSubject] = useState("Video Production Inquiry");

  const packages = useMemo(
    () => [
      {
        title: "Social Media Ad",
        price: "$250–$500",
        blurb: "Quick, clean, and built to perform on Instagram, TikTok, and YouTube Shorts.",
        bullets: [
          "30–60 seconds",
          "Light edit + polish",
          "Royalty-free music",
          "Delivered in vertical + horizontal",
        ],
      },
      {
        title: "Business Overview",
        price: "$500–$1,200",
        blurb: "The “meet the business” video: story, credibility, and visuals that sell.",
        bullets: [
          "2–4 minutes",
          "Interview + b-roll",
          "Basic motion graphics if needed",
          "Great for websites + landing pages",
        ],
      },
      {
        title: "Music Video",
        price: "$1,000–$3,000+",
        blurb: "Planned, directed, and edited with a cinematic feel. Your track; your world.",
        bullets: [
          "Creative planning + shot list",
          "Performance + b-roll coverage",
          "Color grade + final polish",
          "Multiple export versions",
        ],
      },
    ],
    []
  );

  const featuredVideoIds = useMemo(
    () => [
      "AeTvUyrjSug",
      "6UqjRHTa5fc",
      "-VOM4PbtqDk",
      "e9hjRtHjOV4",
      "wXFl6xuVz6s",
      "9NeJU6s2Lx0",
    ],
    []
  );

  const scrollToContact = (presetSubject) => {
    if (presetSubject) setSubject(presetSubject);

    if (!contactRef.current) return;
    const y = contactRef.current.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  const safeJson = async (res) => {
    const text = await res.text();
    try {
      return text ? JSON.parse(text) : {};
    } catch {
      return { message: text };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const form = e.currentTarget;

    const formData = {
      name: form.name.value.trim(),
      email: form.email.value.trim(),
      phone: form.phone.value.trim(),
      subject: subject?.trim() || form.subject?.value?.trim() || "Video Production Inquiry",
      message: form.message.value.trim(),
      sourcePage: "Video Production",
    };

    setSubmitting(true);
    setStatus({ state: "sending", message: "Sending…" });

    try {
      const res = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await safeJson(res);

      if (!res.ok) {
        const msg = data?.message || "There was an issue submitting the form.";
        throw new Error(msg);
      }

      setStatus({ state: "success", message: data?.message || "Sent! We’ll get back to you soon." });
      form.reset();
      setSubject("Video Production Inquiry");
    } catch (err) {
      console.error(err);
      setStatus({
        state: "error",
        message: err?.message || "There was an error submitting the form.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="psoc vp">
      {/* Hero */}
      <section className="vp-hero">
        <div className="psoc-container vp-heroGrid">
          <div className="vp-heroText">
            <p className="psoc-badge">Video Production • Colorado • Fast Turnaround</p>
            <h1 className="psoc-h1">Video Production Services</h1>
            <p className="psoc-lead">
              Clean visuals, clear messaging, and a final edit that feels professional, without the messy process.
              Pricing below is a starting point and can flex with scope.
            </p>

            <div className="vp-ctaRow">
              <button className="psoc-btn psoc-btnPrimary" type="button" onClick={() => scrollToContact("Video Production Quote")}>
                Get a Quote
              </button>
              <button className="psoc-btn psoc-btnSecondary" type="button" onClick={() => window.scrollTo({ top: window.innerHeight * 0.9, behavior: "smooth" })}>
                View Packages
              </button>
            </div>

            <div className="vp-stats">
              <div className="vp-stat">
                <div className="vp-statNum">Vertical + Horizontal</div>
                <div className="vp-statLabel">Exports that fit every platform</div>
              </div>
              <div className="vp-stat">
                <div className="vp-statNum">Story → Shoot → Edit</div>
                <div className="vp-statLabel">A smooth, guided process</div>
              </div>
              <div className="vp-stat">
                <div className="vp-statNum">Polished Audio</div>
                <div className="vp-statLabel">Clean dialogue + music balance</div>
              </div>
            </div>

            <p className="vp-relatedLine">
              Also available: <Link className="psoc-link" to="/weddings">Wedding videography</Link>{" "}
              and <Link className="psoc-link" to="/real-estate">Real estate walkthroughs</Link>.
            </p>
          </div>

          <div className="vp-heroVideo" aria-label="Featured work">
            <VideoEmbed id={featuredVideoIds[0]} title="Featured video work" />
            <div className="vp-heroVideoCaption">A quick sample of our style. More below.</div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="psoc-section">
        <div className="psoc-container">
          <div className="psoc-sectionHead">
            <h2 className="psoc-h2">Packages</h2>
            <p className="psoc-sub">
              Pick a direction and we’ll tailor the details. Deliverables, length, location, and style.
            </p>
          </div>

          <div className="vp-grid3">
            {packages.map((p) => (
              <PackageCard
                key={p.title}
                title={p.title}
                price={p.price}
                blurb={p.blurb}
                bullets={p.bullets}
                onSelect={() => scrollToContact(`${p.title} Quote`)}
              />
            ))}
          </div>

          <div className="vp-noteCard">
            <div className="vp-noteTitle">Typical add-ons</div>
            <div className="vp-noteGrid">
              <div className="vp-noteItem">Drone coverage (where applicable)</div>
              <div className="vp-noteItem">Extra deliverables / extra cutdowns</div>
              <div className="vp-noteItem">Script help or interview guidance</div>
              <div className="vp-noteItem">On-location audio upgrades</div>
            </div>
            <div className="vp-noteFoot">
              Not sure what to ask for? Send the goal and platform (website, IG, YouTube) and we’ll recommend the best package.
            </div>
          </div>
        </div>
      </section>

      {/* Featured Work */}
      <section className="psoc-section psoc-sectionAlt">
        <div className="psoc-container">
          <div className="psoc-sectionHead psoc-sectionHeadRow">
            <div>
              <h2 className="psoc-h2">Featured Work</h2>
              <p className="psoc-sub">A few examples. Fast to preview, easy to share.</p>
            </div>

            <button className="psoc-btn psoc-btnGhost" type="button" onClick={() => scrollToContact("Video Production Quote")}>
              Start a Project
            </button>
          </div>

          <div className="psoc-videoGrid">
            {featuredVideoIds.map((id) => (
              <VideoEmbed key={id} id={id} title={`Production Studio Video ${id}`} />
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="psoc-section">
        <div className="psoc-container">
          <div className="psoc-sectionHead">
            <h2 className="psoc-h2">How it works</h2>
            <p className="psoc-sub">Clear steps, no confusion, no awkward surprises.</p>
          </div>

          <div className="vp-processGrid">
            <div className="psoc-card">
              <h3 className="psoc-cardTitle">Creative direction</h3>
              <p className="psoc-cardText">
                You tell us the goal, audience, and vibe. We help shape the plan, length, and shot list.
              </p>
            </div>
            <div className="psoc-card">
              <h3 className="psoc-cardTitle">Filming</h3>
              <p className="psoc-cardText">
                Efficient coverage with attention to lighting, composition, and clean audio when it matters.
              </p>
            </div>
            <div className="psoc-card">
              <h3 className="psoc-cardTitle">Editing + delivery</h3>
              <p className="psoc-cardText">
                Tight pacing, color grade, audio polish, and exports sized for web + social.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="psoc-section psoc-sectionAlt">
        <div className="psoc-container">
          <div className="psoc-sectionHead">
            <h2 className="psoc-h2">FAQ</h2>
            <p className="psoc-sub">Quick answers to the common stuff.</p>
          </div>

          <div className="vp-faq">
            <details className="vp-faqItem">
              <summary>How long does it take?</summary>
              <p>
                Depends on complexity, but most smaller projects deliver fast once filming is complete. If you have a hard deadline,
                include it in your message and we’ll plan around it.
              </p>
            </details>

            <details className="vp-faqItem">
              <summary>Do you help with scripting or what to say?</summary>
              <p>
                Yep. We can help outline talking points, tighten wording, or guide interviews so the final cut feels natural.
              </p>
            </details>

            <details className="vp-faqItem">
              <summary>Can you make versions for different platforms?</summary>
              <p>
                Absolutely. Vertical (Reels/TikTok) and horizontal (YouTube/website) exports are common deliverables.
              </p>
            </details>

            <details className="vp-faqItem">
              <summary>What should I send you to get a quote?</summary>
              <p>
                Goal, platform, location, ideal length, and any examples you like. If you don’t have examples, just describe the vibe.
              </p>
            </details>
          </div>

          <div className="vp-relatedCtas">
            <div className="vp-relatedCard">
              <div className="vp-relatedTitle">Need a wedding film instead?</div>
              <p className="vp-relatedText">Cinematic wedding coverage with clean audio and intentional editing.</p>
              <Link className="psoc-btn psoc-btnSecondary" to="/weddings">
                Weddings
              </Link>
            </div>

            <div className="vp-relatedCard">
              <div className="vp-relatedTitle">Selling a home or listing?</div>
              <p className="vp-relatedText">High-fidelity walkthroughs with optional drone coverage.</p>
              <Link className="psoc-btn psoc-btnSecondary" to="/real-estate">
                Real Estate
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="psoc-section" ref={contactRef} id="contact">
        <div className="psoc-container">
          <div className="psoc-sectionHead">
            <h2 className="psoc-h2">Get a quote</h2>
            <p className="psoc-sub">
              Tell us what you want to create (goal, platform, timeline, and any references), and we’ll respond by email.
            </p>
          </div>

          <div className="psoc-contactCard">
            <form onSubmit={handleSubmit} className="psoc-form">
              <div className="psoc-formGrid">
                <div className="psoc-field">
                  <label className="psoc-label" htmlFor="name">Name</label>
                  <input id="name" name="name" type="text" placeholder="Name" required autoComplete="name" />
                </div>

                <div className="psoc-field">
                  <label className="psoc-label" htmlFor="email">Email</label>
                  <input id="email" name="email" type="email" placeholder="Email" required autoComplete="email" />
                </div>

                <div className="psoc-field">
                  <label className="psoc-label" htmlFor="phone">Phone</label>
                  <input id="phone" name="phone" type="tel" placeholder="Phone (optional)" autoComplete="tel" />
                </div>

                <div className="psoc-field">
                  <label className="psoc-label" htmlFor="subject">Subject</label>
                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    placeholder="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </div>

                <div className="psoc-field psoc-fieldFull">
                  <label className="psoc-label" htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Tell us about your project… what’s the goal, where will it be used, and what’s your timeline?"
                    required
                    rows={7}
                  />
                </div>
              </div>

              <div className="psoc-formFooter">
                <button className="psoc-btn psoc-btnPrimary" type="submit" disabled={submitting}>
                  {submitting ? "Sending…" : "Submit"}
                </button>

                <div className="psoc-status" aria-live="polite">
                  {status.state !== "idle" && (
                    <span className={`psoc-statusMsg psoc-${status.state}`}>{status.message}</span>
                  )}
                </div>
              </div>
            </form>

            <p className="psoc-emailNote">
              Or email us directly at{" "}
              <a className="psoc-link" href="mailto:productionstudioofcolorado@gmail.com">
                productionstudioofcolorado@gmail.com
              </a>
            </p>
          </div>
        </div>
      </section>

      <footer className="psoc-footer">
        <div className="psoc-container psoc-footerInner">
          <span>© {new Date().getFullYear()} Production Studio of Colorado LLC</span>
          <button
            className="psoc-btn psoc-btnGhost"
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            Back to top
          </button>
        </div>
      </footer>
    </div>
  );
};

export default VideoProduction;
